import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  TextField,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import type { Warehouse } from '../types/inventory.types';

interface DeleteAllItemsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  deleting: boolean;
  progress: number;
  deletedCount: number;
  totalCount: number;
  selectedWarehouse: Warehouse | null;
}

export const DeleteAllItemsDialog: React.FC<DeleteAllItemsDialogProps> = ({
  open,
  onClose,
  onConfirm,
  deleting,
  progress,
  deletedCount,
  totalCount,
  selectedWarehouse,
}) => {
  const [confirmationText, setConfirmationText] = useState<string>('');
  const REQUIRED_TEXT = 'ONAYLIYORUM';
  const isConfirmed = confirmationText.trim().toUpperCase() === REQUIRED_TEXT;

  // Modal açıldığında/kapandığında input'u temizle
  useEffect(() => {
    if (!open) {
      setConfirmationText('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (isConfirmed) {
      onConfirm();
      setConfirmationText(''); // İşlem sonrası temizle
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!deleting ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningIcon />
        {selectedWarehouse ? `'${selectedWarehouse.name}' Deposundaki Tüm Ürünleri Sil` : 'Tüm Stokları Sil'}
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            🚨 KRİTİK UYARI: Bu işlem geri alınamaz!
          </Typography>
        </Alert>

        <DialogContentText sx={{ mb: 2 }}>
          {selectedWarehouse 
            ? <>
                <Box component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  '{selectedWarehouse.name}' deposundaki toplam {totalCount} adet ürün
                </Box> kalıcı olarak silinecektir.
              </>
            : <>
                <Box component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  Sistemdeki tüm stok ürünleri ({totalCount} adet)
                </Box> kalıcı olarak silinecektir.
              </>
          }
        </DialogContentText>
        
        <Box sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 1, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="error.main">
            ⚠️ Bu işlemin sonuçları:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • Bu silme işlemini <strong>geri alamazsınız</strong>
          </Typography>
          <Typography variant="body2">
            • {selectedWarehouse 
              ? `'${selectedWarehouse.name}' deposundaki tüm ürünler silinecek`
              : 'Tüm depolardaki tüm ürünler silinecek'}
          </Typography>
          <Typography variant="body2">
            • Silinecek toplam ürün sayısı: <strong>{totalCount} adet</strong>
          </Typography>
          <Typography variant="body2" color="error.main">
            • İlgili tüm veriler kalıcı olarak kaybolacak
          </Typography>
        </Box>

        {!deleting && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" fontWeight="bold" color="error.main" gutterBottom>
              İşlemi onaylamak için aşağıdaki kutuya büyük harflerle "<strong>ONAYLIYORUM</strong>" yazın:
            </Typography>
            <TextField
              fullWidth
              autoFocus
              variant="outlined"
              placeholder="ONAYLIYORUM yazın"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              disabled={deleting}
              sx={{ mt: 1 }}
              error={confirmationText.length > 0 && !isConfirmed}
              helperText={
                confirmationText.length > 0 && !isConfirmed
                  ? 'Lütfen tam olarak "ONAYLIYORUM" yazın (büyük harflerle)'
                  : isConfirmed
                  ? '✓ Onay metni doğru'
                  : ''
              }
              inputProps={{
                style: { textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }
              }}
            />
          </Box>
        )}
        
        {deleting && (
          <Box sx={{ width: '100%', mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Silme işlemi devam ediyor...
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              İlerleme: {deletedCount} / {totalCount} ({progress}%)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress variant="determinate" value={progress} color="error" />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${progress}%`}</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={onClose} 
          color="primary"
          disabled={deleting}
          variant="outlined"
        >
          İptal
        </Button>
        <Button 
          onClick={handleConfirm} 
          color="error" 
          variant="contained"
          disabled={deleting || !isConfirmed}
          startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
        >
          {deleting ? 'Siliniyor...' : `${totalCount} Ürünü Sil`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

